import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isLimitedAgent } from '../config/roleAccess';
import { limitedModule, limitedWritten, LIMITED_HIDDEN_ROUTES } from '../data/limitedAgentTraining';
import { isCertExcluded } from '../shared/teamLeadCertAccess';
import { writtenTrainings } from '../data/writtenTrainings';

import { Home, BookOpen, Target, Presentation, Search, Video, MapPin, Magnet, Users, Star, RefreshCw, Sparkles } from 'lucide-react';
import { agentSetupModule } from '../data/agentSetupTraining';
import { newAgentsModule } from '../data/newAgentsTraining';
import { buyersTrainingModule } from '../data/buyersTraining';
import { fastCapTrainingModule } from '../data/fastCapTraining';
import { aiAgentsModule } from '../data/aiAgentsTraining';
import { howToUseResiModule } from '../data/howToUseResiTraining';
import { leadGenModule, openHousesModule, youtubeModule, farmingModule, leadMagnetsModule, facebookGroupsModule, referralsReviewsModule, expiredsModule, sellersListingsModule } from '../data/knowledgeBase';

// Every training module that can be searched, with the page that opens it.
const writtenText = (id) => { const w = writtenTrainings[id]; return w ? [w.intro, ...(w.steps || []), ...(w.tips || [])] : []; };

const SEARCHABLE_MODULES = [
  { module: agentSetupModule, route: '/training/onboarding' },
  { module: howToUseResiModule, route: '/training/how-to-use-resi' },
  { module: newAgentsModule, route: '/training/new-agents' },
  { module: aiAgentsModule, route: '/training/ai-agents' },
  { module: buyersTrainingModule, route: '/training/buyers' },
  { module: fastCapTrainingModule, route: '/training/fastcap' },
  { module: leadGenModule, route: '/training/lead-generation' },
  { module: openHousesModule, route: '/training/open-houses' },
  { module: youtubeModule, route: '/training/youtube' },
  { module: farmingModule, route: '/training/farming' },
  { module: leadMagnetsModule, route: '/training/lead-magnets' },
  { module: facebookGroupsModule, route: '/training/facebook-groups' },
  { module: referralsReviewsModule, route: '/training/referrals-reviews' },
  { module: expiredsModule, route: '/training/expireds' },
  { module: sellersListingsModule, route: '/training/sellers-listings' },
];

// Optional photo for each tile or the hero. Set image to a URL (for example a photo in the public folder) to show it.
const HERO_IMAGE = null;

const TILES = [
  { title: 'Agent Onboarding', text: 'The step-by-step setup for new agents, starting with the welcome.', Icon: Home, route: '/training/onboarding', image: '/training-photos/key-door.jpg' },
  { title: 'Resi Setup', text: 'Required for everyone, even experienced agents. Your full Resi profile, Phone Link, your AI connection, and BoldTrail.', Icon: Home, route: '/training/resi-setup', image: '/training-photos/resi-setup.jpg' },
  { title: 'How to Use Resi', text: 'A step-by-step tour of every part of Resi, from Tasks and Leads to Transactions, Open Houses and Tools, with a short quiz after each section.', Icon: BookOpen, route: '/training/how-to-use-resi', image: '/training-photos/resi-setup.jpg' },
  { title: 'Team Lead Certification', text: 'Required before you receive team leads. Short lessons on follow-up, daily Resi activity and tasks, then a quiz.', Icon: Star, route: '/training/team-lead-certification', image: '/training-photos/resi-setup.jpg', certOnly: true },
  { title: 'Basic Skills', text: 'MLS search and CMAs, plus contracts and writing an offer.', Icon: BookOpen, route: '/training/new-agents', image: '/training-photos/basic-skills.jpg' },
  { title: 'AI for Agents', text: 'Simple, everyday ways to use AI to work faster, from email and listings to contracts and your weekly routine.', Icon: Sparkles, route: '/training/ai-agents', image: '/training-photos/ai-for-agents.jpg' },
  { title: 'Working with Buyers', text: 'Consultations, showings, objections, writing and presenting offers, and closing.', Icon: Users, route: '/training/buyers', image: '/training-photos/working-with-buyers.jpg' },
  { title: 'Fast Cap - Self Paced', text: 'The Fast Cap self-paced course: 7 modules and 22 lessons.', Icon: BookOpen, route: '/training/fastcap', image: '/training-photos/fast-cap.jpg' },
  { title: 'BoldTrail CRM & Website', text: 'BoldTrail setup, websites and landing pages, follow-up, and more.', Icon: Target, route: '/training/lead-generation', image: '/training-photos/boldtrail-crm.jpg' },
  { title: 'Open Houses', text: 'Finding, preparing for, hosting, and following up on open houses.', Icon: Presentation, route: '/training/open-houses', image: '/training-photos/open-house-interior.jpg' },
  { title: 'Farming', text: 'Choosing your farm, staying top of mind, and planning prospecting and marketing.', Icon: MapPin, route: '/training/farming', image: '/training-photos/farming.jpg' },
  { title: 'Lead Magnets', text: 'What lead magnets are, how to choose them, and how to build a landing page.', Icon: Magnet, route: '/training/lead-magnets', image: '/training-photos/lead-magnets.jpg' },
  { title: 'Facebook Groups', text: 'Generating leads through your own Facebook group.', Icon: Users, route: '/training/facebook-groups', image: '/training-photos/facebook-groups.jpg' },
  { title: 'Expireds & Terminated', text: 'Prospecting expired and terminated listings with mailers, follow-up, and tracking.', Icon: RefreshCw, route: '/training/expireds', image: '/training-photos/expireds.jpg' },
  { title: 'Building Referrals and Reviews', text: 'Home anniversary notes to past clients and review requests after closing.', Icon: Star, route: '/training/referrals-reviews', image: '/training-photos/referrals-reviews.jpg' },
  { title: 'Sellers & Listings', text: 'Preparing for listing appointments, CMAs, and listing presentations.', Icon: Home, route: '/training/sellers-listings', image: '/training-photos/sellers-listings.jpg' },
  { title: 'YouTube', text: 'Planning, filming, and growing your real estate YouTube channel.', Icon: Video, route: '/training/youtube', image: '/training-photos/youtube.jpg' },
];

const stripHtml = (text) => String(text || '');

// RESIDENCE marketing templates, opened from the bar at the top of Training.

// Kelly's Zoom personal meeting room. Live trainings happen here.
const ZOOM_ROOM_URL = 'https://zoom.us/j/9799511871';

const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const GOLD = '#D4AF37';

const styles = {
  page: { minHeight: '100vh', background: '#f5f6f8', padding: '24px 16px', fontFamily: FONT, boxSizing: 'border-box' },
  wrap: { maxWidth: 960, margin: '0 auto' },
  hero: {
    borderRadius: 18,
    padding: '28px 24px 24px',
    marginBottom: 20,
    background: HERO_IMAGE
      ? `linear-gradient(90deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.7) 60%, rgba(255,255,255,0.3) 100%), url(${HERO_IMAGE}) center / cover`
      : '#ffffff',
    border: '1px solid #e6e1d3',
  },
  h1: { fontSize: 30, fontWeight: 800, color: '#1a1a1a', margin: '0 0 6px', letterSpacing: '-0.01em' },
  muted: { color: '#6b6b6b', fontSize: 15, margin: '0 0 18px', lineHeight: 1.5 },
  searchWrap: { position: 'relative' },
  searchIcon: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '13px 18px 13px 46px',
    fontSize: 15,
    border: '1px solid #d8c9a8',
    borderRadius: 999,
    fontFamily: FONT,
    background: '#ffffff',
    color: '#1a1a1a',
    outline: 'none',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 20 },
  tile: {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    textDecoration: 'none',
    background: '#ffffff',
    border: '1px solid #e6e1d3',
    borderTop: `4px solid ${GOLD}`,
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    fontFamily: FONT,
    color: '#1a1a1a',
    padding: 0,
  },
  photo: { height: 110, background: 'linear-gradient(135deg, #f8f0d8 0%, #ead9a8 100%)' },
  tileBody: { padding: '0 20px 22px' },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: '#fbf3da',
    border: '3px solid #ffffff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '-28px 0 12px',
  },
  tileTitle: { fontSize: 19, fontWeight: 700, margin: '0 0 6px', color: '#1a1a1a' },
  tileText: { fontSize: 14, color: '#6b6b6b', margin: 0, lineHeight: 1.5 },
  resultList: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 },
  resultButton: { textAlign: 'left', background: '#ffffff', border: '1px solid #e6e1d3', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', fontFamily: FONT },
  resultTitle: { fontSize: 15, fontWeight: 700, color: '#1a1a1a', margin: 0 },
  resultMeta: { fontSize: 12, color: '#7f7f7f', marginTop: 4 },
  noResults: { fontSize: 14, color: '#6b6b6b', marginBottom: 20 },
  liveCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    background: '#ffffff',
    border: '1px solid #e6e1d3',
    borderLeft: `4px solid ${GOLD}`,
    borderRadius: 0,
    padding: '18px 22px',
    marginBottom: 20,
  },
  liveTitle: { fontSize: 16, fontWeight: 700, color: '#1a1a1a' },
  liveText: { fontSize: 13, color: '#6b6b6b', marginTop: 4 },
  liveButton: {
    padding: '12px 20px',
    background: '#000000',
    color: '#ffffff',
    border: '1px solid #000000',
    borderRadius: 0,
    fontWeight: 700,
    fontSize: 13,
    textDecoration: 'none',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap',
  },
};

const HOVER_CSS = `
  .tt-tile { transition: transform 0.18s ease, box-shadow 0.18s ease; }
  .tt-tile:hover { transform: translateY(-3px); box-shadow: 0 12px 26px rgba(0,0,0,0.12); }
  .tt-tile:focus-visible { outline: 3px solid #D4AF37; outline-offset: 3px; }
`;

function Tile({ tile, onNavigate }) {
  const { title, text, Icon, href, image } = tile;
  const photoStyle = image
    ? { ...styles.photo, backgroundImage: `url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : styles.photo;
  const content = (
    <>
      <div style={photoStyle} />
      <div style={styles.tileBody}>
        <div style={styles.iconWrap}>
          <Icon size={26} color={GOLD} aria-hidden="true" />
        </div>
        <div style={styles.tileTitle}>{title}</div>
        <p style={styles.tileText}>{text}</p>
      </div>
    </>
  );
  if (href) {
    return (
      <a className="tt-tile" href={href} target="_blank" rel="noopener noreferrer" style={styles.tile}>
        {content}
      </a>
    );
  }
  return (
    <button className="tt-tile" type="button" onClick={() => onNavigate(tile.route)} style={styles.tile}>
      {content}
    </button>
  );
}

export default function TrainingHome() {
  const { user, viewAsUser, userRole } = useAuth();
  const limited = isLimitedAgent(userRole);
  const hideCert = isCertExcluded({}, viewAsUser ? viewAsUser.email : user?.email);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const allSteps = useMemo(
    () =>
      SEARCHABLE_MODULES.map(({ module, route }) => ({ module: limited ? limitedModule(module) : module, route })).filter((x) => x.module).flatMap(({ module, route }) =>
        module.sections.flatMap((section) =>
          section.steps.map((step) => ({
            key: `${module.id}-${step.id}`,
            title: step.title,
            body: [...(step.body || []).map(stripHtml), ...(limited ? (() => { const w = limitedWritten(writtenTrainings[step.id]); return w ? [w.intro, ...(w.steps || []), ...(w.tips || [])] : []; })() : writtenText(step.id)), ...(step.tipBox ? [step.tipBox.title, ...(step.tipBox.items || [])] : [])].join(' '),
            moduleTitle: module.title,
            sectionTitle: section.title,
            route,
          }))
        )
      ),
    [limited]
  );

  const term = query.trim().toLowerCase();
  const results = term
    ? allSteps
        .filter((step) =>
          [step.title, step.body, step.sectionTitle, step.moduleTitle].some((text) =>
            text.toLowerCase().includes(term)
          )
        )
        .slice(0, 30)
    : [];

  return (
    <div style={styles.page}>
      <style>{HOVER_CSS}</style>
      <div style={styles.wrap}>
        <div style={styles.hero}>
          <h1 style={styles.h1}>Training</h1>
          <p style={styles.muted}>Start with onboarding if you're new. Use the knowledge base for how we work with buyers, sellers, and leads.</p>
          <div style={styles.searchWrap}>
            <Search size={18} color="#8a8a8a" style={styles.searchIcon} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search training topics, like MLS, contracts, or BoldTrail"
              aria-label="Search training topics"
              style={styles.searchInput}
            />
          </div>
        </div>

        <div style={styles.liveCard}>
          <div>
            <div style={styles.liveTitle}>Join a Live Training</div>
            <div style={styles.liveText}>Join Kelly's Zoom room for live trainings. Check the team calendar for upcoming times.</div>
          </div>
          <a href={ZOOM_ROOM_URL} target="_blank" rel="noopener noreferrer" style={styles.liveButton}>Join Zoom Room</a>
        </div>

        {term && (
          results.length === 0 ? (
            <div style={styles.noResults}>No training topics match "{query.trim()}". Try a different word.</div>
          ) : (
            <div style={styles.resultList}>
              {results.map((step) => (
                <button key={step.key} type="button" onClick={() => navigate(step.route)} style={styles.resultButton}>
                  <p style={styles.resultTitle}>{step.title}</p>
                  <div style={styles.resultMeta}>{step.moduleTitle} · {step.sectionTitle}</div>
                </button>
              ))}
            </div>
          )
        )}

        {!term && (
          <div style={styles.grid}>
            {TILES.filter((tile) => !(tile.certOnly && hideCert) && !(limited && LIMITED_HIDDEN_ROUTES.includes(tile.route))).map((tile) => (
              <Tile key={tile.title} tile={tile} onNavigate={navigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
